// admin#8 / A-1 — 平台 admin 全域(跨租戶)Ops 租戶治理 read model。
// 對接 restful `GET /v3/admin/ops/orgs`(+`/{org_id}`):每 org 的 live Ops
// 產品 entitlement、Plan 承載的配額上限(max_seats/max_devices,OPEN-54-1)
// 與觀測用量(席次/裝置/節點 + 超限旗標)。
//
// 唯讀觀測面:Ops 開通 / 停用 / 降級 一律走既有 Plan-grant 商業鏈
// (/plan-grants?subject=org:{org_id}),此面不提供 mutation。

import { apiFetch, normalizeCursor } from '../../lib/api';

export interface OrgOpsPlan {
  plan_id: string;
  plan_version: number;
  plan_name?: string;
  grant_id?: string;
  max_seats: number;
  max_devices: number;
}

export interface OrgOpsUsage {
  seats_used: number;
  devices_used: number;
  node_count: number;
  seats_over_limit?: boolean;
  devices_over_limit?: boolean;
}

export interface OrgOpsGovernanceRow {
  org_id: string;
  name: string;
  state: string;
  owner_user_id?: string;
  ops_entitled: boolean;
  plan?: OrgOpsPlan | null;
  usage: OrgOpsUsage;
}

export interface OpsGovernanceListParams {
  state?: string;
  query?: string;
  cursor?: string;
  limit?: number;
  entitledOnly?: boolean;
}

export interface OpsGovernanceListPage {
  items: OrgOpsGovernanceRow[];
  nextCursor: string | null;
  limit: number | null;
}

interface OpsGovernanceListEnvelope {
  items?: OrgOpsGovernanceRow[];
  meta?: {
    next_cursor?: string | null;
    limit?: number;
  };
}

export async function listOpsGovernanceOrgs(params: OpsGovernanceListParams = {}): Promise<OpsGovernanceListPage> {
  const res = await apiFetch<OpsGovernanceListEnvelope>('/v3/admin/ops/orgs', {
    query: {
      state: params.state,
      q: params.query,
      cursor: params.cursor,
      limit: params.limit,
      entitled: params.entitledOnly ? '1' : undefined,
    },
  });
  return {
    items: Array.isArray(res.data?.items) ? res.data.items : [],
    nextCursor: normalizeCursor(res.data?.meta?.next_cursor),
    limit: res.data?.meta?.limit ?? null,
  };
}

export async function getOpsGovernanceOrg(orgId: string): Promise<OrgOpsGovernanceRow> {
  const res = await apiFetch<OrgOpsGovernanceRow>(`/v3/admin/ops/orgs/${encodeURIComponent(orgId)}`);
  return res.data;
}

// ---- presentation helpers (pure) ----

/** Deep-link to the Plan-grant governance surface prefilled with the org subject. */
export function orgGrantSubjectLink(orgId: string): string {
  return `/plan-grants?subject=${encodeURIComponent(`org:${orgId}`)}`;
}

export function stateLabel(state: string | undefined): string {
  const labels: Record<string, string> = {
    approved: '已核准',
    pending: '待審核',
    applied: '待審核',
    rejected: '已拒絕',
    suspended: '已停權',
  };
  return state ? labels[state] ?? '其他狀態' : '其他狀態';
}

export function stateTone(state: string | undefined): string {
  switch (state) {
    case 'approved':
      return 'good';
    case 'pending':
    case 'applied':
      return 'warn';
    case 'rejected':
    case 'suspended':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** A zero Plan ceiling means "no limit at this layer" — never render it as 0/limit. */
export function ceilingLabel(max: number | undefined): string {
  return max && max > 0 ? String(max) : '未設上限';
}

export interface QuotaView {
  used: number;
  max: number;
  /** ratio in [0,1] for the meter; null when there is no positive ceiling */
  ratio: number | null;
  over: boolean;
  tone: 'good' | 'warn' | 'danger' | 'neutral';
  label: string;
}

/** Derive a quota meter view from used vs a Plan ceiling. Pure + tested. */
export function quotaView(used: number, max: number | undefined, over: boolean | undefined): QuotaView {
  const ceiling = max && max > 0 ? max : 0;
  if (ceiling <= 0) {
    return { used, max: 0, ratio: null, over: false, tone: 'neutral', label: `${used} / 未設上限` };
  }
  const ratio = Math.min(used / ceiling, 1);
  const isOver = over ?? used > ceiling;
  let tone: QuotaView['tone'] = 'good';
  if (isOver) tone = 'danger';
  else if (used / ceiling >= 0.8) tone = 'warn';
  return { used, max: ceiling, ratio, over: isOver, tone, label: `${used} / ${ceiling}` };
}
